import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from "@/services/api";


// Helper: Get all dates between two dates (inclusive) formatted as YYYY-MM-DD
const getDatesInRange = (startDateStr, endDateStr) => {
  const dates = [];
  let currentDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  
  // Strip time for accurate day comparison
  currentDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

// Async Thunks
export const fetchLeaves = createAsyncThunk('leave/fetchLeaves', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(`/admin/get-leaves`);
    return response;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const fetchLeaveReasons = createAsyncThunk('leave/fetchLeaveReasons', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(`/admin/get-leave-reasons`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const applyLeave = createAsyncThunk('leave/applyLeave', async (leaveData, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.post(`/admin/apply-leave`, leaveData);
    // Re-fetch leaves to update the calendar immediately after successful application
    dispatch(fetchLeaves());
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const deleteLeave = createAsyncThunk('leave/deleteLeave', async (leaveData, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.post(`/admin/delete-leave`, leaveData);
    return response;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const fetchAllUsersLeaves = createAsyncThunk(
  'leave/fetchAllUsersLeaves',
  async (formdata, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/get-all-users-leaves`, formdata);
      return response.data; 
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

const leaveSlice = createSlice({
  name: 'leave',
  initialState: {
    appliedLeavesDates: [], // Array of YYYY-MM-DD strings for easy calendar rendering
    appliedLeaves: [], // Store the full leave objects for reference
    leaveReasons: [],
    isLoading: false,
    isApplying: false,
    isDeleting: false,
    error: null,
    allUsersLeaves: [], // Store all users' leaves for admin view
    list_loading:false, // Loading state for fetching all users' leaves
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Leaves
      .addCase(fetchLeaves.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchLeaves.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.status) {
          // Flatten all leave ranges into a single array of formatted date strings
          const allDates = new Set();
          action.payload.data.forEach(leave => {
            if (leave.status === 'approved') { // Only show approved leaves as green dots
              const dates = getDatesInRange(leave.leave_from, leave.leave_to);
              dates.forEach(date => allDates.add(date));
            }
          });
          state.appliedLeaves = action.payload.data; // Store the full leave objects for reference
          state.appliedLeavesDates = Array.from(allDates);
        }
      })
      .addCase(fetchLeaves.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch leaves';
      })

      // Fetch Leave Reasons
      .addCase(fetchLeaveReasons.fulfilled, (state, action) => {
        if (action.payload) {
          state.leaveReasons = action.payload;
        }
      })

      // Apply Leave
      .addCase(applyLeave.pending, (state) => {
        state.isApplying = true;
      })
      .addCase(applyLeave.fulfilled, (state) => {
        state.isApplying = false;
      })
      .addCase(applyLeave.rejected, (state, action) => {
        state.isApplying = false;
        state.error = action.payload?.message || 'Failed to apply leave';
      })
      .addCase(deleteLeave.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteLeave.fulfilled, (state) => {
        state.isDeleting = false;
      })
      .addCase(deleteLeave.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload?.message || 'Failed to delete leave';
      })
      .addCase(fetchAllUsersLeaves.pending, (state) => {
        state.list_loading = true;
      })
      .addCase(fetchAllUsersLeaves.fulfilled, (state, action) => {
        state.list_loading = false;
        const payload = action.payload;
        console.log("Fetched all users' leaves:", payload);
        if (!payload || payload.current_page === 1 || !state.allUsersLeaves?.data) {
          state.allUsersLeaves = payload;
        } else {
          state.allUsersLeaves = {
            ...payload,
            data: [...state.allUsersLeaves.data, ...payload.data],
          };
        }
      })
      .addCase(fetchAllUsersLeaves.rejected, (state, action) => {
        state.list_loading = false;
        state.error = action.payload?.message || "Failed to fetch all users' leaves";
      });
  },
});

export const { clearError } = leaveSlice.actions;
export default leaveSlice.reducer;